import { useAppSelector } from '@web/redux/store';
import I18njs from 'i18n-js';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import PublicPage from '../../components/Shared/Public/PublicPage';
import { loginRequest, sendFallbackMfaCodeRequest } from '../../redux/User/actions';
import LoginForm, { LoginFormValues } from './Login';
import MfaForm, { MfaFormValues } from './Mfa';
import { MfaTypes } from './MfaTypes';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

type LoginStep = 'LOGIN' | 'MFA' | 'EMAIL_MFA';

export const Login = () => {
  const dispatch = useDispatch();
  const error = useAppSelector((state) => state.user.error);
  const loading = useAppSelector((state) => state.user.loading);
  const [formData, setFormData] = useState<LoginFormValues>({
    company: '',
    username: '',
    password: '',
  });
  const [step, setStep] = useState<LoginStep>('LOGIN');

  const handleSubmit = (data: LoginFormValues) => {
    setFormData(data);
    dispatch(
      loginRequest({
        ...data,
        timezone,
        cb: () => setStep('MFA'),
      }),
    );
  };

  const handlePhoneMfaSubmit = ({ code }: MfaFormValues) => {
    dispatch(
      loginRequest({
        ...formData,
        code,
        timezone,
      }),
    );
  };

  const handleEmailMfaSubmit = ({ code: fallbackToken }: MfaFormValues) => {
    dispatch(
      loginRequest({
        ...formData,
        fallbackToken,
        timezone,
      }),
    );
  };

  const resendPhoneCode = () => {
    handleSubmit(formData);
  };

  const resendEmailCode = () => {
    dispatch(sendFallbackMfaCodeRequest(formData));
  };

  const switchMethod = (type: MfaTypes) => {
    switch (type) {
      case MfaTypes.Email:
        resendEmailCode();
        return setStep('EMAIL_MFA');

      default:
        resendPhoneCode();
        return setStep('MFA');
    }
  };

  return (
    <>
      <Helmet>
        <title>{I18njs.t('helmet.Login')}</title>
      </Helmet>
      <PublicPage>
        <>
          {step === 'LOGIN' && <LoginForm onSubmit={handleSubmit} errorMsg={error?.message} loading={loading} />}
          {step === 'MFA' && (
            <MfaForm
              type={MfaTypes.Phone}
              loading={loading}
              resend={resendPhoneCode}
              requestSwitch={switchMethod}
              onSubmit={handlePhoneMfaSubmit}
              errorMsg={error?.message}
            />
          )}
          {step === 'EMAIL_MFA' && (
            <MfaForm
              disableTypeSwitch
              type={MfaTypes.Email}
              errorMsg={error?.message}
              loading={loading}
              resend={resendEmailCode}
              onSubmit={handleEmailMfaSubmit}
            />
          )}
        </>
      </PublicPage>
    </>
  );
};

export default Login;
